import { Eye, Pencil, Trash2 } from 'lucide-react';
import { useMemo } from 'react';

/**
 * Hook to build row actions for CRUD list views
 * Generates standard actions (view, edit, delete, remove) based on config
 * and combines them with custom actions
 */
function useCrudActions({ config, customActions = [], baseUrl, getValueFromItem, navigate, onDelete, onRemove, onConfirmAction, }) {
    const actionConfig = config.listView.actions || {};
    return useMemo(() => {
        const actions = [];
        // View action
        if (actionConfig.view?.enabled) {
            const viewConfig = actionConfig.view;
            actions.push({
                key: 'view',
                label: viewConfig.label || 'View',
                icon: Eye,
                variant: 'ghost',
                getUrl: (item) => {
                    if (viewConfig.getUrl) {
                        return viewConfig.getUrl(item);
                    }
                    const itemId = getValueFromItem(item);
                    return baseUrl ? `${baseUrl}/${itemId}` : config.endpoints.single(itemId);
                },
                onClick: (item, event) => {
                    // Handle custom onClick if provided
                    if (viewConfig.onClick) {
                        viewConfig.onClick(item);
                        return;
                    }
                    // If Cmd/Ctrl+click, let the DataTable handle opening in new window
                    if (event && (event.metaKey || event.ctrlKey)) {
                        return;
                    }
                    // Normal click - navigate
                    if (viewConfig.getUrl) {
                        navigate(viewConfig.getUrl(item));
                    }
                    else {
                        const itemId = getValueFromItem(item);
                        const url = baseUrl ? `${baseUrl}/${itemId}` : config.endpoints.single(itemId);
                        navigate(url);
                    }
                },
            });
        }
        // Edit action
        if (actionConfig.edit?.enabled) {
            const editConfig = actionConfig.edit;
            actions.push({
                key: 'edit',
                label: editConfig.label || 'Edit',
                icon: Pencil,
                variant: 'ghost',
                getUrl: (item) => {
                    if (editConfig.getUrl) {
                        return editConfig.getUrl(item);
                    }
                    const itemId = getValueFromItem(item);
                    return baseUrl
                        ? `${baseUrl}/${itemId}/edit`
                        : `${config.endpoints.single(itemId)}/edit`;
                },
                onClick: (item, event) => {
                    // Handle custom onClick if provided
                    if (editConfig.onClick) {
                        editConfig.onClick(item);
                        return;
                    }
                    // If Cmd/Ctrl+click, let the DataTable handle opening in new window
                    if (event && (event.metaKey || event.ctrlKey)) {
                        return;
                    }
                    // Normal click - navigate
                    if (editConfig.getUrl) {
                        navigate(editConfig.getUrl(item));
                    }
                    else {
                        const itemId = getValueFromItem(item);
                        const url = baseUrl
                            ? `${baseUrl}/${itemId}/edit`
                            : `${config.endpoints.single(itemId)}/edit`;
                        navigate(url);
                    }
                },
            });
        }
        // Delete action
        if (actionConfig.delete?.enabled) {
            const deleteConfig = actionConfig.delete;
            const deleteAction = {
                key: 'delete',
                label: deleteConfig.label || 'Delete',
                variant: 'destructive',
                icon: Trash2,
                onClick: onDelete,
                confirm: {
                    title: deleteConfig.confirm?.title || `Delete ${config.entityName}`,
                    description: deleteConfig.confirm?.description ||
                        `Are you sure you want to delete this ${config.entityName.toLowerCase()}? This action cannot be undone.`,
                    confirmText: deleteConfig.confirm?.confirmText || 'Delete',
                    cancelText: deleteConfig.confirm?.cancelText || 'Cancel',
                },
            };
            actions.push({
                key: 'delete',
                label: deleteAction.label,
                icon: deleteAction.icon,
                variant: 'destructive',
                onClick: (item) => onConfirmAction(deleteAction, item),
            });
        }
        // Remove action
        if (actionConfig.remove?.enabled) {
            const removeConfig = actionConfig.remove;
            const removeAction = {
                key: 'remove',
                label: removeConfig.label || 'Remove',
                variant: 'destructive',
                icon: Trash2,
                onClick: onRemove,
                confirm: {
                    title: removeConfig.confirm?.title || `Remove ${config.entityName}`,
                    description: removeConfig.confirm?.description ||
                        `Are you sure you want to remove this ${config.entityName.toLowerCase()}?`,
                    confirmText: removeConfig.confirm?.confirmText || 'Remove',
                    cancelText: removeConfig.confirm?.cancelText || 'Cancel',
                },
            };
            actions.push({
                key: 'remove',
                label: removeAction.label,
                icon: removeAction.icon,
                variant: 'destructive',
                onClick: (item) => onConfirmAction(removeAction, item),
            });
        }
        // Add custom actions
        return [
            ...actions,
            ...customActions.map(action => ({
                key: action.key,
                label: action.label,
                variant: action.variant,
                icon: action.icon,
                onClick: (item) => {
                    if (action.confirm) {
                        onConfirmAction(action, item);
                    }
                    else {
                        action.onClick(item);
                    }
                },
            })),
        ];
    }, [
        actionConfig,
        customActions,
        config,
        baseUrl,
        getValueFromItem,
        onDelete,
        onRemove,
        onConfirmAction,
        navigate,
    ]);
}

export { useCrudActions };
//# sourceMappingURL=useCrudActions.js.map
